import React, { useMemo, useCallback, useContext, useEffect } from 'react';
import Toggle from '@atlaskit/toggle';
import FilterIcon from '@atlaskit/icon/glyph/filter';
import Lozenge from '@atlaskit/lozenge';
import { useQueryParams } from 'use-query-params';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import upperFirst from 'lodash/upperFirst';
import Button from '@atlaskit/button/new';
import MediaServicesActualSizeIcon from '@atlaskit/icon/glyph/media-services/actual-size';
import MediaServicesFitToPageIcon from '@atlaskit/icon/glyph/media-services/fit-to-page';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import { SloGroupCohortSummaryDTO } from '@atlassiansox/phobos-client-ts';
import { OptionType } from '@atlaskit/select';

import {
  flex,
  flexCenter,
  flexStart,
  marginPushRight,
  spaceGapCompact,
  spaceGapCozy,
  spaceGapMinimum,
  spaceTopCozy,
} from '@/styles';
import { filterStore, useAutoCompleteController } from '@/controllers';
import { StageDisplayContext, StageDisplayType } from '@/controllers/stage-display-controller';

import { generalBarStyles } from '../styles';
import AdvancedDropdown from '../advanced-dropdown';
import { CohortMemberCohortTypeEnum } from '../types';

import { filterBarStyles } from './styles';
import { CohortType, sloGroupSearchObjectType } from './types';

type FilterBarProps = {
  baseLabel: string;
  sloGroupCohortSummaries: SloGroupCohortSummaryDTO[];
  publicId: string;
  cohortTypeSorted: string[];
  hideCohortType?: boolean;
  hideCohortFilters?: boolean;
};

const FilterBar: React.FC<FilterBarProps> = ({
  baseLabel,
  sloGroupCohortSummaries,
  publicId,
  hideCohortType,
  cohortTypeSorted,
  hideCohortFilters,
}) => {
  const { search } = useLocation();

  // Find custom cohort filters
  const customFilterOptions: OptionType[] = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, ...cohortTypeSortedRest] = cohortTypeSorted;
    return cohortTypeSortedRest
      .filter(cohort => sloGroupCohortSummaries.some(summary => summary.cohort === cohort))
      .map(
        (cohort: CohortMemberCohortTypeEnum) =>
          ({
            label: upperFirst(cohort.toLowerCase()),
            value: cohort.toLowerCase(),
          } as OptionType)
      );
  }, [sloGroupCohortSummaries, cohortTypeSorted]);

  // we need to enable the query set for cohortType so the url can be sent to
  // other users, hence it's easier to derive the state from the query params
  // directly  instead of useState variables
  const {
    hasBreachStatusOnly,
    hideDrafts,
    isDraft,
    cohortType,
    pageBase,
    geos,
    regions,
    shards,
    tenants,
    environments,
    shardNames,
    cloudIds,
    customFilters,
    customFiltersList,
  } = useMemo(() => {
    const parsed: sloGroupSearchObjectType = qs.parse(search);
    return {
      hasBreachStatusOnly: parsed.hasBreachStatusOnly,
      hideDrafts: parsed.hideDrafts,
      isDraft: parsed.isDraft,
      pageBase: parsed.pageBase,
      cohortType: parsed.cohortType as string | undefined,
      geos: parsed.geos,
      regions: parsed.regions,
      shards: parsed.shards,
      tenants: parsed.tenants,
      environments: parsed.environments,
      shardNames: parsed.shardNames,
      cloudIds: parsed.cloudIds,
      customFilters: parsed.customFilters,
      customFiltersList: (parsed.customFilters || '').split(','),
    };
  }, [search]);
  const [, setQuery] = useQueryParams({});
  const hasAnyFilterApplied =
    hasBreachStatusOnly ||
    hideDrafts ||
    cohortType ||
    pageBase ||
    geos ||
    regions ||
    shards ||
    tenants ||
    environments ||
    shardNames ||
    cloudIds ||
    customFilters ||
    isDraft;
  const { setStageDisplayType, stageDisplayType } = useContext(StageDisplayContext);
  const isFullScreen = stageDisplayType === StageDisplayType.FullScreen;

  const handleResetFilters = useCallback(() => {
    setQuery({
      hasBreachStatusOnly: undefined,
      hideDrafts: undefined,
      isDraft: undefined,
      cohortType: undefined,
      pageBase: undefined,
      geos: undefined,
      regions: undefined,
      shards: undefined,
      tenants: undefined,
      environments: undefined,
      shardNames: undefined,
      cloudIds: undefined,
      customFilters: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHasBreachStatusFilterChange = useCallback(() => {
    setQuery({
      hasBreachStatusOnly: hasBreachStatusOnly ? undefined : true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBreachStatusOnly]);

  const handleHideDraftsFilterChange = useCallback(() => {
    setQuery({
      hideDrafts: hideDrafts ? undefined : true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideDrafts]);

  // Set the filter state based on the query params and so we ensure the only
  // source of truth is the URL query params
  const { actions: filterActions } = useContext(filterStore);
  useEffect(() => {
    filterActions.setSLOHasBreachingOnly(hasBreachStatusOnly === 'true');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBreachStatusOnly]);

  const handleToggleFullScreen = useCallback(() => {
    setStageDisplayType(
      stageDisplayType === StageDisplayType.FullScreen
        ? StageDisplayType.Normal
        : StageDisplayType.FullScreen
    );
  }, [setStageDisplayType, stageDisplayType]);

  // Cohort type filter
  const handleCohortTypeChange = useCallback((value: string[]) => {
    if (!value.length) {
      setQuery({ cohortType: undefined });
      return;
    }
    setQuery({ cohortType: value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Custom filters
  const handleCustomFiltersChange = useCallback((value: string[]) => {
    if (!value.length) {
      setQuery({
        customFilters: undefined,
        geos: undefined,
        regions: undefined,
        shards: undefined,
      });
      return;
    }
    setQuery({ customFilters: value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cohort filter
  const handleCohortChange = useCallback((scope: string, value: string[]) => {
    if (!value.length) {
      setQuery({ [scope]: undefined });
      return;
    }
    setQuery({ [scope]: value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadGeoOptions = useAutoCompleteController('slogroup', {
    formatPlainOption: true,
    typePublicId: publicId,
    params: `level=${CohortMemberCohortTypeEnum.Geo}`,
  });
  const loadRegionOptions = useAutoCompleteController('slogroup', {
    formatPlainOption: true,
    typePublicId: publicId,
    params: `level=${CohortMemberCohortTypeEnum.Region}`,
  });
  const loadShardOptions = useAutoCompleteController('slogroup', {
    formatPlainOption: true,
    typePublicId: publicId,
    params: `level=${CohortMemberCohortTypeEnum.Shard}`,
  });

  // TODO: implement tenant options when tenants cohort are available in mimir SLO groups
  const loadTenantOptions = useAutoCompleteController('slogroup', {
    formatPlainOption: true,
    typePublicId: publicId,
    // params: `level=${CohortMemberCohortTypeEnum.Tenant}`,
  });

  return (
    <div css={[flexStart, spaceGapCompact, spaceTopCozy, generalBarStyles, filterBarStyles]}>
      <div css={[flexStart, spaceGapCozy]}>
        <FilterIcon size="small" label="filter" />
        <div css={[flexStart, spaceGapMinimum]}>
          <span css={[flexStart, spaceGapMinimum]}>
            Breaching <Lozenge>only</Lozenge>
          </span>
          <Toggle
            onChange={handleHasBreachStatusFilterChange}
            isChecked={hasBreachStatusOnly === 'true'}
          />
        </div>
        <div css={[flexStart, spaceGapMinimum]}>
          <span css={[flexStart, spaceGapMinimum]}>
            Hide <Lozenge appearance="moved">🛠 Draft</Lozenge>
          </span>
          <Toggle onChange={handleHideDraftsFilterChange} isChecked={hideDrafts === 'true'} />
        </div>
      </div>
      {!hideCohortType && (
        <AdvancedDropdown
          label="Cohort type"
          onChange={handleCohortTypeChange}
          value={cohortType}
          options={[
            { label: 'Rollup', value: CohortType.Rollup },
            { label: `${upperFirst(baseLabel)}`, value: CohortType.Base },
          ]}
          closeMenuOnSelect
        />
      )}
      <>
        {cohortTypeSorted.map((type: string) => {
          const customFilter: string = type.toLowerCase();
          if (customFiltersList.includes(customFilter)) {
            let value: string[] = [];
            let loadOptions;
            let isSearchable = false;
            let menuHeight;
            switch (customFilter) {
              case 'geo':
                value = geos?.split(',') || [];
                loadOptions = loadGeoOptions;
                isSearchable = false;
                menuHeight = 'auto';
                break;
              case 'region':
                value = regions?.split(',') || [];
                loadOptions = loadRegionOptions;
                isSearchable = true;
                menuHeight = 350;
                break;
              case 'shard':
                value = shards?.split(',') || [];
                loadOptions = loadShardOptions;
                isSearchable = true;
                menuHeight = 350;
                break;
              // TODO: implement tenant options when tenants cohort are available in mimir SLO groups
              case 'tenant':
                value = tenants?.split(',') || [];
                loadOptions = loadTenantOptions;
                isSearchable = true;
                menuHeight = 350;
                break;
            }
            return (
              <AdvancedDropdown
                label={upperFirst(customFilter)}
                onChange={(v: string[]) => handleCohortChange(`${customFilter}s`, v)}
                placeholder={`Search by ${customFilter} name`}
                value={value}
                loadOptions={loadOptions}
                isAsync
                isMulti
                isSearchable={isSearchable}
                menuWidth={360}
                menuHeight={menuHeight}
              />
            );
          }
          return null;
        })}
      </>

      {!hideCohortFilters && (
        <AdvancedDropdown
          label="Cohort filters"
          onChange={handleCustomFiltersChange}
          value={customFilters ? customFilters.split(',') : undefined}
          options={customFilterOptions}
          isMulti
          staticTrigger
          labelReset="Remove all custom filters"
          appearance="subtle"
          iconAfter={<EditorAddIcon label="" size="small" />}
          rerenderMenuOnSelect
        />
      )}

      <div css={[marginPushRight, flex, spaceGapCompact]}>
        <Button appearance="subtle" isDisabled={!hasAnyFilterApplied} onClick={handleResetFilters}>
          Clear filters
        </Button>
        <span css={[flexCenter, spaceGapMinimum]}>
          <Button onClick={handleToggleFullScreen}>
            {isFullScreen ? (
              <MediaServicesFitToPageIcon label="" size="small" />
            ) : (
              <MediaServicesActualSizeIcon label="" size="small" />
            )}
          </Button>
        </span>
      </div>
    </div>
  );
};

export default FilterBar;
